// src/util/flatPromise.ts
function flatPromise(executor) {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  if (executor) {
    executor(resolve, reject);
  }
  return { promise, resolve, reject };
}

// src/util/string.ts
function stringHasPrefix(str, word) {
  return str.lastIndexOf(word, 0) === 0;
}

// src/util/url.ts
var originRegex = /^((?:\w+:)?\/\/([^\/]+))/;
function encodeQuery(queryParams) {
  let out = [];
  const k = Object.keys(queryParams);
  const eu = encodeURIComponent;
  for (let i = 0; i < k.length; i++) {
    out.push(`${eu(k[i])}=${eu(queryParams[k[i]])}`);
  }
  return out.join("&");
}
function originOf(url) {
  const l = document.location;
  if (stringHasPrefix(url, "/") || stringHasPrefix(url, ".")) {
    if (l.origin)
      return l.origin;
    return l.protocol + "//" + l.host;
  }
  const match = url.match(originRegex);
  if (!match)
    throw new Error("Invalid URL: " + url);
  return match[1];
}

// src/communication/iframeTarget.ts
var IFrameCommunicationTarget = class {
  constructor(opts) {
    /**
     * We have received a message from this target at any point
     */
    this.ready = false;
    /**
     * Messages that couldn't be delivered yet as the target isn't ready to receive messages.
     */
    this.buffer = [];
    this.id = opts.id;
    this.type = opts.type;
    this.element = opts.element;
    this.onReady = opts.onReady;
    this.origin = originOf(opts.element.src);
  }
  send(msg) {
    if (this.ready) {
      this.element.contentWindow.postMessage(msg, this.origin);
    } else {
      this.buffer.push(msg);
    }
  }
  setReady(ready) {
    this.onReady();
    this.ready = ready;
    if (this.ready) {
      this.flush();
    }
  }
  flush() {
    for (let i = 0; i < this.buffer.length; i++) {
      this.element.contentWindow.postMessage(this.buffer[i], this.origin);
    }
    this.buffer = [];
  }
};

// src/communication/bus.ts
function isAllowedOrigin(origin, allowedOrigins) {
  return origin === "*" || allowedOrigins.has(origin);
}
var CommunicationBus = class {
  constructor() {
    /**
     * Messages sent from this set of origins will be considered, all others are ignored.
     * Perhaps the website this code runs on has more cross-origin message passing happening, we don't want to interfere.
     */
    this.origins = /* @__PURE__ */ new Set();
    // We use a map here to prevent the need to add a Map polyfill in the widget.
    this.targets = {};
    /** Some messages that expect an answer may be handled twice if two SDKs are present. Here we keep track of those and deliver them only once. */
    this.answered = /* @__PURE__ */ new Set();
    /**
     * Called upon receiving a message intended for consumption by the root itself, which is the host page
     * that contains the widgets and agent iframes.
     */
    this.onReceiveRootMessage = () => {
    };
    window.addEventListener("message", (ev) => {
      this.onReceive(ev);
    });
  }
  /**
   * Adds a listener for root messages.
   * @internal
   */
  listen(onReceiveRootMessage) {
    let orig = this.onReceiveRootMessage;
    this.onReceiveRootMessage = (msg) => {
      orig(msg);
      onReceiveRootMessage(msg);
    };
  }
  /**
   * Add an origin to allow messages from.
   * @internal
   */
  addOrigin(origin) {
    this.origins.add(origin);
  }
  /**
   * Send from the local root
   * @param msg
   * @internal
   */
  send(msg) {
    if (msg.from_id) {
      const messageSender = this.targets[msg.from_id];
      if (!messageSender) {
        console.error(`[bus] Unexpected message from unknown sender ${msg.from_id}`, msg);
        return;
      }
      if (msg.type === "widget_announce" || msg.type === "agent_announce") {
        messageSender.setReady(true);
      }
    }
    const rid = msg.rid;
    if (rid) {
      if (this.answered.has(rid + msg.to_id)) {
        return;
      }
      this.answered.add(rid + msg.to_id);
    }
    if (msg.to_id === "") {
      this.onReceiveRootMessage(msg);
      return;
    }
    const messageTarget = this.targets[msg.to_id];
    if (!messageTarget) {
      console.error(`[bus] Unexpected message to unknown target ${msg.to_id}`, msg);
      return;
    }
    messageTarget.send(msg);
  }
  onReceive(ev) {
    if (!isAllowedOrigin(ev.origin, this.origins)) {
      console.debug("Friendly Captcha communication bus ignored message from origin " + ev.origin, this.origins);
      return;
    }
    const msg = ev.data;
    if (!msg || !msg._frc)
      return;
    this.send(msg);
  }
  /**
   * @param ct
   * @internal
   */
  registerTarget(ct) {
    this.targets[ct.id] = ct;
  }
  /**
   * @internal
   */
  registerTargetIFrame(type, id, iframe, timeout) {
    const fp = flatPromise();
    let timeoutPromise = new Promise((resolve) => setTimeout(() => resolve("timeout"), timeout));
    const t = new IFrameCommunicationTarget({
      id,
      element: iframe,
      type,
      onReady: () => fp.resolve("registered")
    });
    this.registerTarget(t);
    return Promise.race([fp.promise, timeoutPromise]);
  }
  /**
   * @internal
   */
  removeTarget(id) {
    delete this.targets[id];
  }
};

// src/util/random.ts
function randomId(length, chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789") {
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
}

// node_modules/idb-keyval/dist/index.js
function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.oncomplete = request.onsuccess = () => resolve(request.result);
    request.onabort = request.onerror = () => reject(request.error);
  });
}
function createStore(dbName, storeName) {
  const request = indexedDB.open(dbName);
  request.onupgradeneeded = () => request.result.createObjectStore(storeName);
  const dbp = promisifyRequest(request);
  return (txMode, callback) => dbp.then((db) => callback(db.transaction(storeName, txMode).objectStore(storeName)));
}
var defaultGetStoreFunc;
function defaultGetStore() {
  if (!defaultGetStoreFunc) {
    defaultGetStoreFunc = createStore("keyval-store", "keyval");
  }
  return defaultGetStoreFunc;
}
function get(key, customStore = defaultGetStore()) {
  return customStore("readonly", (store) => promisifyRequest(store.get(key)));
}
function set(key, value, customStore = defaultGetStore()) {
  return customStore("readwrite", (store) => {
    store.put(value, key);
    return promisifyRequest(store.transaction);
  });
}

// src/sdk/persist.ts
var SESSION_COUNT_KEY = "frc_sc";
var SESSION_ID_KEY = "frc_sid";
var SEPARATOR = "__";
var didIncrease = false;
var sc = "0";
var sid = "__" + randomId(10);
function sessionCount(increase) {
  if (!didIncrease) {
    let scnumber = 0;
    try {
      scnumber = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) || "", 10);
    } catch (e) {
    }
    if (isNaN(scnumber))
      scnumber = 0;
    increase && scnumber++;
    sc = scnumber.toString();
    try {
      sessionStorage.setItem(SESSION_COUNT_KEY, sc);
    } catch (e) {
    }
  }
  return sc;
}
function sessionId() {
  let id;
  try {
    id = sessionStorage.getItem(SESSION_ID_KEY);
  } catch (e) {
    return sid;
  }
  if (!id) {
    id = randomId(12);
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}
var Store = class {
  constructor(prefix) {
    /**
     * Fallback memory-only store
     */
    this.mem = /* @__PURE__ */ new Map();
    this.storePrefix = prefix;
  }
  /**
   * @returns Returns whether we have storage access
   */
  setup() {
    return new Promise((resolve, reject) => {
      if (this._hasSA !== void 0) {
        return resolve(this._hasSA);
      }
      try {
        indexedDB.open("");
      } catch (e) {
        return resolve(this._hasSA = false);
      }
      if (!document.hasStorageAccess) {
        return resolve(this._hasSA = true);
      }
      document.hasStorageAccess().then((hasSA) => {
        this._hasSA = hasSA;
        if (!this._hasSA) {
          console.debug("FRC has no storage access");
        } else {
          this.idb = createStore("friendlycaptcha", "frc");
        }
        return resolve(this._hasSA);
      }).catch(reject);
    });
  }
  get(key, opts) {
    return this.setup().then((hasSA) => {
      const storeKey = this.storePrefix + SEPARATOR + key;
      if (opts.p) {
        if (hasSA)
          return get(storeKey, this.idb);
        return this.mem.get(key);
      }
      try {
        const sessValue = sessionStorage.getItem(storeKey);
        return sessValue === null ? void 0 : sessValue;
      } catch (e) {
      }
      return this.mem.get(key);
    });
  }
  set(key, value, opts) {
    return this.setup().then((hasSA) => {
      const storeKey = this.storePrefix + SEPARATOR + key;
      if (opts.p) {
        if (hasSA)
          return set(storeKey, value, this.idb);
        if (value === void 0) {
          this.mem.delete(key);
        } else {
          this.mem.set(key, value);
        }
      } else {
        try {
          if (value === void 0) {
            this.mem.delete(key);
            sessionStorage.removeItem(storeKey);
          } else {
            this.mem.set(key, value);
            sessionStorage.setItem(storeKey, value);
          }
        } catch (e) {
        }
      }
    });
  }
  /** Has storage access, populated after any `set` or `get` has been completed. */
  hasSA() {
    return this._hasSA;
  }
};

// src/sdk/supports.ts
var supportAllowClipboardWrite = typeof navigator !== "undefined" && navigator.userAgentData !== void 0;

// src/sdk/dom.ts
function findCaptchaElements() {
  const elements = document.querySelectorAll(".frc-captcha");
  return elements;
}
function findParentFormElement(element) {
  while (element.tagName !== "FORM") {
    element = element.parentElement;
    if (!element) {
      return null;
    }
  }
  return element;
}
function executeOnceOnFocusInEvent(element, listener) {
  element.addEventListener("focusin", listener, { once: true, passive: true });
}
function styleIfNotAlreadySet(el, name, value) {
  if (el.style[name] === "") {
    el.style[name] = value;
  }
}
function setWidgetRootStyles(el) {
  const sinas = styleIfNotAlreadySet;
  sinas(el, "position", "relative");
  sinas(el, "height", "70px");
  sinas(el, "padding", "0");
  sinas(el, "width", "316px");
  sinas(el, "maxWidth", "100%");
  sinas(el, "maxHeight", "100%");
  sinas(el, "overflow", "hidden");
  sinas(el, "borderRadius", "4px");
}
function removeWidgetRootStyles(el) {
  el.removeAttribute("style");
}
function fireFRCEvent(element, eventData) {
  let event;
  if (typeof window.CustomEvent === "function") {
    event = new CustomEvent(eventData.name, {
      bubbles: true,
      detail: eventData
    });
  } else {
    event = document.createEvent("CustomEvent");
    event.initCustomEvent(eventData.name, true, false, eventData);
  }
  element.dispatchEvent(event);
}
function findFirstParentLangAttribute(element) {
  while (!element.lang) {
    element = element.parentElement;
    if (!element) {
      return null;
    }
  }
  return element.lang;
}

// src/sdk/create.ts
var FRAME_ID_DATASET_FIELD = "FrcFrameId";
var AGENT_FRAME_CLASSNAME = "frc-i-agent";
var WIDGET_FRAME_CLASSNAME = "frc-i-widget";
var WIDGET_PLACEHOLDER_CLASSNAME = "frc-widget-placeholder";
function createAgentIFrame(frcSDK, agentId, src) {
  const frameParams = {
    origin: document.location.origin,
    sess_id: sessionId(),
    sess_c: sessionCount(true),
    comm_id: agentId,
    sdk_v: "0.1.12",
    v: "1",
    agent_id: agentId,
    ts: Date.now().toString()
  };
  const el = document.createElement("iframe");
  el.className = AGENT_FRAME_CLASSNAME;
  el.dataset[FRAME_ID_DATASET_FIELD] = agentId;
  el.src = src + "?" + encodeQuery(frameParams);
  el.frcSDK = frcSDK;
  const s = el.style;
  s.width = s.height = s.border = s.visibility = "0";
  s.display = "none";
  return el;
}
function createWidgetIFrame(agentId, widgetId, widgetUrl, opts) {
  const el = document.createElement("iframe");
  let language = opts.language;
  if (!language || language === "html") {
    language = findFirstParentLangAttribute(opts.element) || "";
  }
  const frameData = {
    origin: document.location.origin,
    sess_id: sessionId(),
    sess_c: sessionCount(true),
    comm_id: widgetId,
    sdk_v: "0.1.12",
    v: "1",
    agent_id: agentId,
    lang: language,
    sitekey: opts.sitekey || "",
    ts: Date.now().toString()
  };
  if (opts.theme) {
    frameData.theme = opts.theme;
  }
  if (supportAllowClipboardWrite) {
    el.allow = "clipboard-write";
  }
  el.frameBorder = "0";
  el.src = widgetUrl + "?" + encodeQuery(frameData);
  el.className = WIDGET_FRAME_CLASSNAME;
  el.dataset[FRAME_ID_DATASET_FIELD] = widgetId;
  const s = el.style;
  s.border = s.visibility = "0";
  s.position = "absolute";
  s.height = s.width = "100%";
  s.display = "none";
  opts.element.appendChild(el);
  return el;
}
function createWidgetPlaceholder(opts) {
  const el = document.createElement("div");
  el.classList.add(WIDGET_PLACEHOLDER_CLASSNAME);
  const s = el.style;
  const isDark = opts.theme === "dark" || opts.theme === "auto" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  s.color = isDark ? "#fff" : "#222";
  s.backgroundColor = isDark ? "#171717" : "#fafafa";
  s.borderRadius = "4px";
  s.border = "1px solid";
  s.borderColor = "#ddd";
  s.padding = "8px";
  s.height = s.width = "100%";
  s.fontSize = "14px";
  setCommonTextStyles(s);
  opts.element.appendChild(el);
  return el;
}
function setCommonTextStyles(s) {
  s.textDecoration = s.fontStyle = "none";
  s.fontWeight = "500";
  s.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  s.lineHeight = "1";
  s.letterSpacing = "-0.0125rem";
}
function createBanner(opts) {
  const el = document.createElement("div");
  el.classList.add("frc-banner");
  const els = el.style;
  els.position = "absolute";
  els.bottom = "2px";
  els.right = "6px";
  els.lineHeight = "1";
  const a = document.createElement("a");
  a.href = "https://friendlycaptcha.com";
  a.rel = "noopener";
  const s = a.style;
  setCommonTextStyles(s);
  s.fontSize = "10px";
  s.color = "#777";
  s.letterSpacing = "-0.0125rem";
  a.target = "_blank";
  a.textContent = "Friendly Captcha";
  a.onmouseenter = () => s.textDecoration = "underline";
  a.onmouseleave = () => s.textDecoration = "none";
  el.appendChild(a);
  opts.element.appendChild(el);
}

// src/util/performance.ts
function windowPerformanceNow() {
  const p = window.performance;
  return p ? p.now() : 0;
}

// src/signals/trigger.ts
function getTrigger(type, startMode, el, ev) {
  const t = windowPerformanceNow();
  const bcr = el.getBoundingClientRect();
  const trigger = {
    v: 1,
    tt: type,
    pnow: t,
    sm: startMode,
    el: {
      bcr: [bcr.left, bcr.top, bcr.width, bcr.height],
      con: document.body.contains(el)
    },
    stack: new Error().stack || "",
    we: !!window.event,
    weit: !!window.event && !!window.event.isTrusted
  };
  if (ev) {
    trigger.ev = {
      ts: ev.timeStamp,
      rt: !!ev.relatedTarget,
      // @ts-ignore: not present in every browser
      eot: !!ev.explicitOriginalTarget,
      it: ev.isTrusted
    };
  }
  return trigger;
}

// src/util/object.ts
function mergeObject(target, ...sources) {
  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    for (var key in source) {
      if (source.hasOwnProperty(key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
}

// src/sdk/widgetHandle.ts
var DEFAULT_FORM_FIELD_NAME = "frc-captcha-response";
var WidgetHandle = class {
  /**
   * You don't want to create this yourself, use `FriendlyCaptcha.createWidget` instead.
   * @internal
   */
  constructor(opts) {
    this.state = "init";
    this.response = ".UNINITIALIZED";
    this.focusEventPending = false;
    /**
     * When this is true the widget has been destroyed and can no longer be used.
     */
    this.isDestroyed = false;
    this.id = opts.id;
    const createOpts = opts.createOpts;
    this.e = createOpts.element;
    this.ready = opts.registered;
    if (!this.e)
      throw new Error("No element provided to mount widget under.");
    this.e.frcWidget = this;
    this.formFieldName = createOpts.formFieldName === void 0 ? DEFAULT_FORM_FIELD_NAME : createOpts.formFieldName;
    this.sitekey = createOpts.sitekey;
    this._reset = opts.callbacks.onReset;
    this._destroy = opts.callbacks.onDestroy;
    this._trigger = opts.callbacks.onTrigger;
    this.startMode = opts.createOpts.startMode || "focus";
    if (this.formFieldName !== null) {
      const iel = document.createElement("input");
      iel.type = "hidden";
      iel.style.display = "none";
      iel.name = this.formFieldName;
      this.hiddenFormEl = iel;
      this.e.appendChild(iel);
    }
    this.setState({ response: ".UNCONNECTED", state: "init" });
    this.ready.then(() => {
      this.handleStartMode();
    });
  }
  handleStartMode() {
    if (this.startMode === "focus" && !this.focusEventPending && !this.isDestroyed) {
      const formElement = findParentFormElement(this.e);
      if (formElement) {
        this.focusEventPending = true;
        executeOnceOnFocusInEvent(formElement, (ev) => {
          this.trigger("focus", { ev });
          this.focusEventPending = false;
        });
      }
    } else if (this.startMode === "auto") {
      this.trigger("auto");
    }
  }
  /**
   * Reset the widget, removing any progress.
   *
   * Optional argument: an object with the name of the trigger that caused the reset.
   * You would usually keep this empty. This is the `trigger` field in the `frc:widget.reset` event, which defaults to `root`.
   */
  reset(opts = { trigger: "root" }) {
    if (this.isDestroyed)
      throw new Error("Can not reset destroyed widget.");
    this.setState({ response: ".RESET", state: "reset", resetTrigger: opts.trigger });
    this._reset(opts);
    this.handleStartMode();
  }
  /**
   * Destroy the widget.
   *
   * This removes the `element` that the widget was mounted to as well as the hidden `frc-captcha-response` form field.
   */
  destroy() {
    this.isDestroyed = true;
    this.hiddenFormEl && this.hiddenFormEl.remove();
    this.hiddenFormEl = void 0;
    this.setState({ response: ".DESTROYED", state: "destroyed" });
    this._destroy();
  }
  /**
   * @internal
   */
  trigger(triggerType, data = {}) {
    if (this.isDestroyed)
      throw new Error("Can not start destroyed widget.");
    const trigger = getTrigger(triggerType, this.startMode, this.e, data.ev);
    this._trigger({ trigger });
  }
  /**
   * Trigger the widget to start a challenge.
   * The widget will start a challenge solving in the background.
   *
   * * In `interactive` mode, the user will need to click the widget to complete the process.
   * * In `noninteractive` mode, the widget will complete the process automatically.
   *
   */
  start() {
    this.trigger("programmatic");
  }
  /**
   * Sets the state of the widget, this is for internal use.
   * It is unlikely this is useful to call yourself.
   * @internal
   */
  setState(s) {
    const didStateChange = this.state !== s.state;
    this.response = s.response;
    this.state = s.state;
    if (this.hiddenFormEl && this.e.isConnected !== false) {
      this.hiddenFormEl.value = s.response;
    }
    if (didStateChange) {
      this.dispatchWidgetEvent({ name: "frc:widget.statechange", error: s.error });
    }
    if (this.state === "expired") {
      this.dispatchWidgetEvent({ name: "frc:widget.expire" });
    } else if (this.state === "completed") {
      this.dispatchWidgetEvent({ name: "frc:widget.complete" });
    } else if (this.state === "error") {
      this.dispatchWidgetEvent({ name: "frc:widget.error", error: s.error });
    } else if (this.state === "reset") {
      this.dispatchWidgetEvent({ name: "frc:widget.reset", trigger: s.resetTrigger });
    }
  }
  dispatchWidgetEvent(eventData) {
    const ed = {
      response: this.response,
      state: this.state,
      id: this.id
    };
    mergeObject(ed, eventData);
    fireFRCEvent(this.e, ed);
  }
  /**
   * Shorthand for `this.getElement().addEventListener`  (that is strictly typed in Typescript)
   */
  addEventListener(type, listener, options) {
    this.e.addEventListener(type, listener, options);
  }
  /**
   * Shorthand for `this.getElement().removeEventListener` (that is strictly typed in Typescript)
   */
  removeEventListener(type, listener, options) {
    this.e.removeEventListener(type, listener, options);
  }
  /**
   * The current state of the widget.
   */
  getState() {
    return this.state;
  }
  /**
   * The current response of the widget. This is the value that should be sent to the server and is embedded in HTML forms.
   */
  getResponse() {
    return this.response;
  }
  /**
   * The HTML element that contains the widget.
   */
  getElement() {
    return this.e;
  }
};

// src/signals/collectStacktrace.ts
var isFunc = function(value) {
  return typeof value === "function";
};
var takeRecords = function() {
  const queue = [];
  const origPatchMap = /* @__PURE__ */ new Map();
  const takeRecords2 = function() {
    const records = queue.splice(0, queue.length);
    return records;
  };
  const w = window;
  const $window = function getRealmSafely() {
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      (document.body || document.head).appendChild(iframe);
      const $window2 = iframe ? iframe.contentWindow : 0;
      iframe.remove();
      return $window2 || w;
    } catch (e) {
      return w;
    }
  }();
  const originalFuncToString = Function.prototype.toString;
  const newFuncToString = function toString(...args) {
    const patchedRef = isFunc(this) ? origPatchMap.get(this) : false;
    const ref = this === newFuncToString ? originalFuncToString : patchedRef ? patchedRef : this;
    return originalFuncToString.apply(ref, args);
  };
  Function.prototype.toString = newFuncToString;
  const getStackSafely = function FC_DummyTrace() {
    const Error2 = $window.Error || w.Error;
    return Error2("FriendlyCaptcha_DummyTrace").stack || "";
  };
  const p = "prototype";
  const c = "constructor";
  const dispatchEvent = w.EventTarget ? w.EventTarget[p].dispatchEvent : {};
  const patches = [
    // Getters as non-function
    ["Document." + p + ".documentElement", w.Document[p], "documentElement"],
    ["Element." + p + ".shadowRoot", w.Element[p], "shadowRoot"],
    ["Node." + p + ".nodeType", w.Node[p], "nodeType"],
    // Values holding functions
    ["eval", w, "eval"],
    ["Object.is", w.Object, "is"],
    ["Array." + p + ".slice", w.Array[p], "slice"],
    ["Document." + p + ".querySelectorAll", w.Document[p], "querySelectorAll"],
    ["Document." + p + ".createElement", w.Document[p], "createElement"],
    ["EventTarget." + p + ".dispatchEvent", dispatchEvent, "dispatchEvent"],
    ["Promise." + p + "." + c, w.Promise[p], c]
  ];
  patches.forEach(function([name, target, prop]) {
    const descriptor = Object.getOwnPropertyDescriptor(target, prop);
    const hasGetterOrSetter = descriptor && (descriptor.get || descriptor.set);
    if (prop === c && !(w.Symbol && w.Symbol.species)) {
      return;
    }
    if (!descriptor) {
      return;
    } else if (hasGetterOrSetter) {
      if (!descriptor.get) {
        return;
      }
    } else {
      if (typeof descriptor.value !== "object" && typeof descriptor.value !== "function") {
        return;
      }
    }
    const newAccessor = function fcPatch(...args) {
      const record = {
        d: Date.now(),
        pnow: windowPerformanceNow(),
        n: name,
        st: getStackSafely()
      };
      queue.push(record);
      const v = hasGetterOrSetter ? descriptor.get : descriptor.value;
      if (c === prop) {
        return v;
      }
      return v.apply(this, args);
    };
    try {
      const descriptorValue = hasGetterOrSetter ? descriptor.get ? descriptor.get() : void 0 : descriptor.value();
      if (descriptorValue) {
        newAccessor.length = descriptorValue.length;
        newAccessor.name = descriptorValue.name;
      }
    } catch (e) {
    }
    try {
      const newDescriptor = mergeObject({}, descriptor);
      if (hasGetterOrSetter) {
        newDescriptor.get = newAccessor;
      } else {
        newDescriptor.value = newAccessor;
      }
      Object.defineProperty(target, prop, newDescriptor);
      origPatchMap.set(newAccessor, hasGetterOrSetter ? descriptor.get : descriptor.value);
    } catch (e) {
    }
  });
  return takeRecords2;
}();

// src/signals/online.ts
function buildOnlineMetric() {
  const s = [0, 0, 0, 0, 0, 0, 0];
  return {
    s,
    add(x2) {
      const n = ++s[0];
      const d = x2 - s[1];
      const dN = d / n;
      const dN2 = dN * dN;
      const t1 = d * dN * (n - 1);
      s[1] += dN;
      s[4] += t1 * dN2 * (n * n - 3 * n + 3) + 6 * dN2 * s[2] - 4 * dN * s[3];
      s[3] += t1 * dN * (n - 2) - 3 * dN * s[2];
      s[2] += t1;
      if (n == 1) {
        s[5] = s[6] = x2;
      } else {
        if (x2 < s[5])
          s[5] = x2;
        if (x2 > s[6])
          s[6] = x2;
      }
    }
  };
}

// src/signals/collect.ts
var x = "addEventListener";
var M = Math;
var ssig;
function isAndroidUA() {
  return /Android/i.test(navigator.userAgent);
}
function onOffEventMetric(onEventName, offEventName, retrigger = false, target = document.body) {
  const m = buildOnlineMetric();
  let on = false;
  let ts;
  target[x](onEventName, (ev) => {
    if (!on || retrigger) {
      ts = ev.timeStamp;
      on = true;
    }
  });
  target[x](offEventName, (ev) => {
    if (on) {
      m.add(ev.timeStamp - ts);
      on = false;
    }
  });
  return m.s;
}
function eventCounts(events) {
  const out = [];
  for (let i = 0; i < events.length; i++) {
    out.push(0);
    document[x](events[i], (_) => out[i]++);
  }
  return out;
}
function keyCountMetric() {
  const out = [0, 0, 0, 0, 0, 0, 0, 0];
  const m = {
    8: 1,
    // Backspace
    46: 1,
    // Delete
    9: 2,
    // Tab
    45: 3,
    // Insert
    17: 4,
    // Control
    13: 5,
    // Enter
    37: 6,
    // Arrow keys
    38: 6,
    39: 6,
    40: 6,
    33: 7,
    // Page up/down
    34: 7
  };
  document[x]("keydown", (ev) => {
    const kc = ev.keyCode;
    if (m[kc]) {
      out[m[kc]]++;
    } else if (kc >= 112 && kc <= 123)
      out[0]++;
  });
  return out;
}
function euclidean2d(x0, x1, y0, y1) {
  return M.sqrt(M.pow(x0 - x1, 2) + M.pow(y0 - y1, 2));
}
function vector3Length(x2, y, z) {
  return M.sqrt(M.pow(x2, 2) + M.pow(y, 2) + M.pow(z, 2));
}
function deltaAngle(a, b) {
  let angle = b - a;
  angle += angle > 180 ? -360 : angle < -180 ? 360 : 0;
  return angle;
}
var Signals = class {
  constructor() {
    /** Distinct touch event radius count */
    this.rn = 0;
    /** Counter */
    this.i = 0;
    this.smel = {
      n: 0,
      ts: 0,
      d: 0
    };
    const $ = "mouse";
    const sm = this.smel;
    const updateMouseEnterMouseLeave = (e) => {
      if (!sm.n) {
        sm.fts = e.timeStamp;
        sm.fxy = [e.clientX, e.clientY, e.screenX, e.screenY];
      }
      sm.n++;
      if (e.type === $ + "leave") {
        sm.d += e.timeStamp - sm.ts;
      }
      sm.ts = e.timeStamp;
      sm.xy = [e.clientX, e.clientY];
    };
    const d = document;
    const b = d.body;
    b[x]($ + "enter", updateMouseEnterMouseLeave);
    b[x]($ + "leave", updateMouseEnterMouseLeave);
    this.bh = {
      onoff: {
        kdu: onOffEventMetric("keydown", "keyup"),
        cse: onOffEventMetric("compositionstart", "compositionend"),
        mdu: onOffEventMetric($ + "down", $ + "up"),
        mle: onOffEventMetric($ + "leave", $ + "enter"),
        med: onOffEventMetric($ + "enter", $ + "down", true),
        semd: onOffEventMetric("scrollend", $ + "down", true, d),
        se: onOffEventMetric("scroll", "scrollend", false, d),
        pdc: onOffEventMetric("pointerdown", "pointercancel", true),
        mmc: onOffEventMetric($ + "move", "click", true),
        tse: onOffEventMetric("touchstart", "touchend"),
        fikd: onOffEventMetric("focusin", "keydown", true)
      },
      nev: eventCounts([
        $ + "out",
        "pointercancel",
        "focus",
        "focusin",
        "blur",
        "visibilitychange",
        "copy",
        "paste",
        "cut",
        "contextmenu",
        "click",
        "auxclick",
        "wheel",
        "resize"
      ]),
      nk: keyCountMetric(),
      mov: this.setupMovementMetrics(),
      dm: this.setupMotionMetrics(),
      do: this.setupOrientationMetrics()
    };
  }
  setupMovementMetrics() {
    let intervalHandle = void 0;
    let sample = [];
    const interval = 50;
    const duration = buildOnlineMetric();
    const distance = buildOnlineMetric();
    const vel = buildOnlineMetric();
    const out = {
      t: duration.s,
      v: vel.s,
      d: distance.s,
      ns: 0
    };
    const b = document.body;
    const updateFunc = () => {
      const lastSample = sample[sample.length - 1];
      if (sample.length >= 1e4 / interval || // A reasonable upper bound
      lastSample && (lastSample[0] && this.tm.timeStamp === lastSample[1] || // Last sample was touch and timestamp is unchanged.
      !lastSample[0] && this.mm.timeStamp === lastSample[1])) {
        clearInterval(intervalHandle);
        intervalHandle = void 0;
        if (sample.length === 1) {
          out.ns++;
          sample = [];
          return;
        }
        const firstSample = sample[0];
        duration.add(lastSample[1] - firstSample[1]);
        distance.add(euclidean2d(lastSample[2], firstSample[2], lastSample[3], firstSample[3]));
        for (let i = 1; i < sample.length; i++) {
          const c = sample[i];
          const p = sample[i - 1];
          const dist = euclidean2d(c[2], p[2], c[3], p[3]) * 1e3;
          const dt = c[1] - p[1];
          vel.add(dist / dt);
        }
        sample = [];
        return;
      }
      let evType = 0;
      if (lastSample)
        evType = lastSample[0];
      else if (this.mm && this.tm)
        evType = this.mm.timeStamp > this.tm.timeStamp ? 0 : 1;
      else if (!this.mm)
        evType = 1;
      if (evType) {
        const t = this.tm.touches[0];
        t && sample.push([1, this.tm.timeStamp, t.screenX, t.screenY]);
      } else {
        sample.push([0, this.mm.timeStamp, this.mm.screenX, this.mm.screenY]);
      }
    };
    b[x]("mousemove", (e) => {
      this.mm = e;
      if (intervalHandle === void 0) {
        updateFunc();
        intervalHandle = setInterval(updateFunc, interval);
      }
    });
    let lastRadius = -1;
    b[x]("touchmove", (e) => {
      this.tm = e;
      const t = e.touches[0];
      if (t) {
        const newRadius = t.radiusX + t.radiusY * 1.234;
        if (newRadius !== lastRadius) {
          lastRadius = newRadius;
          this.rn++;
        }
      }
      if (intervalHandle === void 0) {
        updateFunc();
        intervalHandle = setInterval(updateFunc, interval);
      }
    });
    return out;
  }
  /**
   * @internal
   */
  setupMotionMetrics() {
    const acc = buildOnlineMetric();
    const rr = buildOnlineMetric();
    const sig = {
      n: 0,
      ts: 0,
      ac: acc.s,
      rr: rr.s,
      i: 0,
      g: false
    };
    if (!isAndroidUA()) {
      return sig;
    }
    window[x]("devicemotion", (e) => {
      sig.ts = e.timeStamp;
      sig.i = e.interval;
      sig.g = !e.acceleration;
      const a = e.acceleration || e.accelerationIncludingGravity;
      if (a) {
        acc.add(vector3Length(a.x, a.y, a.z));
      }
      const r = e.rotationRate;
      if (r) {
        rr.add(vector3Length(r.alpha, r.beta, r.gamma));
      }
    });
    return sig;
  }
  /**
   * @internal
   */
  setupOrientationMetrics() {
    const gd = buildOnlineMetric();
    const bd = buildOnlineMetric();
    const sig = {
      fts: 0,
      ts: 0,
      gd: gd.s,
      bd: bd.s
    };
    if (!isAndroidUA()) {
      return sig;
    }
    let hasPrevious;
    window[x]("deviceorientation", (e) => {
      if (e.gamma == null || e.beta == null || e.alpha == null)
        return;
      sig.ts = e.timeStamp;
      sig.a = e.alpha;
      sig.b = e.beta;
      sig.g = e.gamma;
      if (!hasPrevious) {
        sig.fts = sig.ts;
        hasPrevious = true;
      } else {
        gd.add(deltaAngle(e.gamma, sig.g));
        bd.add(deltaAngle(sig.b, e.beta));
      }
    });
    return sig;
  }
  /**
   * @internal
   */
  gmm() {
    const e = this.mm;
    return e && {
      xy: [
        e.clientX,
        e.clientY,
        e.screenX,
        e.screenY,
        e.offsetX,
        e.offsetY,
        e.pageX,
        e.pageY,
        e.movementX,
        e.movementY
      ],
      ts: e.timeStamp
    };
  }
  /**
   * @internal
   */
  gtm() {
    const tm = this.tm;
    const tt = tm && tm.touches;
    const tm0 = tt && tt[0];
    return tm && tm0 && {
      id: tm0.identifier,
      xy: [tm0.clientX, tm0.clientY, tm0.screenX, tm0.screenY, tm0.pageX, tm0.pageY],
      r: [tm0.radiusX, tm0.radiusX, tm0.rotationAngle, tm0.force],
      n: tt.length,
      ts: tm.timeStamp,
      rn: this.rn
    };
  }
  /**
   * @internal
   */
  get(widgetId) {
    const b = document.body;
    const w = window;
    const p = w.performance;
    const sig = {
      v: 1,
      i: ++this.i,
      hl: history.length,
      fe: !!window.frameElement,
      wid: widgetId,
      sc: parseInt(sessionCount(false)),
      sid: sessionId(),
      conv: 0,
      t: {
        pnow: windowPerformanceNow(),
        pto: p && p.timeOrigin || 0,
        ts: Date.now()
      },
      dims: {
        d: [
          w.innerWidth,
          w.innerHeight,
          w.outerWidth,
          w.outerHeight,
          w.screenX,
          w.screenY,
          w.pageXOffset,
          w.pageYOffset,
          b.clientWidth,
          b.clientHeight
        ],
        dpr: w.devicePixelRatio
      },
      mel: this.smel,
      mm: this.gmm(),
      tm: this.gtm(),
      bh: this.bh,
      stack: new Error().stack || "",
      trc: takeRecords()
    };
    return sig;
  }
};
function getSignals() {
  return ssig || (ssig = new Signals());
}

// src/sdk/endpoint.ts
var SHORTHANDS = {
  eu: "https://eu.frcapi.com/api/v2/captcha",
  global: "https://global.frcapi.com/api/v2/captcha"
};
function getSDKAPIEndpoint() {
  const m = document.querySelector(`meta[name="frc-api-endpoint"]`);
  if (m)
    return m.content;
  const cs = document.currentScript;
  if (cs) {
    const endpoint = cs.dataset["frcApiEndpoint"];
    if (endpoint)
      return endpoint;
  }
  const we = document.querySelector(".frc-captcha[data-api-endpoint]");
  if (we) {
    const endpoint = we.dataset["apiEndpoint"];
    if (endpoint)
      return endpoint;
  }
  return void 0;
}
function resolveAPIEndpoint(optionValue) {
  if (!optionValue) {
    return SHORTHANDS.global;
  }
  const v = SHORTHANDS[optionValue];
  if (!v)
    return optionValue;
  return v;
}

// src/sdk/sdk.ts
var agentEndpoint = "/agent";
var widgetEndpoint = "/widget";
var FRAME_ID_DATASET_FIELD2 = "FrcFrameId";
var IFRAME_EXP_TIME = 1e3 * 60 * 60 * 36;
var cbus;
var sdkC = 0;
var FriendlyCaptchaSDK = class {
  constructor(opts = {}) {
    /**
     * Multiple agents may be running at the same time, this is the case if someone uses widgets with different endpoints on a single page.
     * This is a mapping from the origin to the IFrame.
     */
    this.agents = /* @__PURE__ */ new Map();
    /**
     * A mapping from the agent ID to its local state.
     */
    this.agentState = /* @__PURE__ */ new Map();
    /**
     * Mapping of widget ID to the widget handle.
     */
    this.widgets = /* @__PURE__ */ new Map();
    this._attached = flatPromise();
    /**
     * A promise that resolves to all the widgets that are currently attached.
     * @public
     */
    this.attached = this._attached.promise;
    /**
     * @internal
     */
    this.signals = getSignals();
    this.baseURL = resolveAPIEndpoint(opts.apiEndpoint || getSDKAPIEndpoint());
    cbus = cbus || new CommunicationBus();
    cbus.addOrigin(originOf(this.baseURL));
    cbus.listen((msg) => this.onReceiveMessage(msg));
    this.bus = cbus;
    sdkC++;
    if (sdkC > 1) {
      console.warn(
        "Multiple Friendly Captcha SDKs created, this is not recommended. Please use a single SDK instance."
      );
    }
    if (opts.startAgent !== false) {
      this.ensureAgentIFrame();
    }
    this.setupPeriodicRefresh();
  }
  onReceiveMessage(msg) {
    if (msg.type === "root_set_response") {
      const w = this.widgets.get(msg.widget_id);
      if (!w) {
        if (sdkC === 1) {
          console.warn(`Received set response message for widget ${msg.widget_id} that doesn't exist`);
        }
        return;
      }
      w.setState(msg);
    } else if (stringHasPrefix(msg.type, "root_store")) {
      this.handleStoreMessage(msg);
    } else if (msg.type === "root_signals_get") {
      this.handleSignalsGetMessage(msg);
    } else if (msg.type === "widget_reset") {
      const w = this.widgets.get(msg.from_id);
      if (!w) {
        if (sdkC === 1) {
          console.warn(`Received reset message for widget ${msg.from_id} that doesn't exist`);
        }
        return;
      }
      w.reset({ trigger: "widget" });
    }
  }
  handleSignalsGetMessage(msg) {
    const sigs = this.signals.get(msg.widget_id);
    this.bus.send({
      type: "root_signals_get_reply",
      from_id: "",
      to_id: msg.from_id,
      _frc: 1,
      rid: msg.rid,
      value: sigs
    });
  }
  handleStoreMessage(msg) {
    const from = msg.from_id;
    const s = this.agentState.get(from);
    if (!s) {
      console.error(`Store not found ${from}`);
      return;
    }
    if (msg.type === "root_store_get") {
      s.store.get(msg.key, { p: msg.p }).then((v) => {
        this.bus.send({
          type: "root_store_get_reply",
          from_id: "",
          to_id: from,
          _frc: 1,
          rid: msg.rid,
          value: v,
          sa: s.store.hasSA()
        });
      });
    } else if (msg.type === "root_store_set") {
      s.store.set(msg.key, msg.value, { p: msg.p }).then(() => {
        this.bus.send({
          type: "root_store_set_reply",
          from_id: "",
          to_id: from,
          _frc: 1,
          rid: msg.rid,
          sa: s.store.hasSA()
        });
      });
    }
  }
  getAPIUrls(apiURL) {
    if (apiURL && typeof apiURL != "string") {
      return {
        agent: apiURL.agent,
        widget: apiURL.widget
      };
    }
    let u = apiURL || this.baseURL;
    return {
      agent: u + agentEndpoint,
      widget: u + widgetEndpoint
    };
  }
  /**
   * Creates an agent IFrame with the given API url (optional). Returns the Agent ID.
   * @param apiURL - The API URL to use, defaults to the one passed in the constructor.
   * @returns
   */
  ensureAgentIFrame(apiURL) {
    const src = this.getAPIUrls(apiURL).agent;
    const origin = originOf(src);
    let agentIFrames = document.getElementsByClassName(AGENT_FRAME_CLASSNAME);
    for (let index = 0; index < agentIFrames.length; index++) {
      const i = agentIFrames[index];
      if (originOf(i.src) === origin && i.dataset[FRAME_ID_DATASET_FIELD2]) {
        return i.dataset[FRAME_ID_DATASET_FIELD2];
      }
    }
    const agentId = "a_" + randomId(12);
    const el = createAgentIFrame(this, agentId, src);
    this.agents.set(origin, el);
    this.agentState.set(agentId, { store: new Store(origin), origin });
    document.body.appendChild(el);
    let retryLoadCounter = 1;
    const registerWithRetry = () => {
      this.bus.registerTargetIFrame("agent", agentId, el, retryLoadCounter * 3e3).then((status) => {
        if (status === "timeout") {
          if (retryLoadCounter > 15) {
            console.error("[Friendly Captcha] Failed to load agent iframe after 15 retries.");
            el.remove();
            this.agents.delete(origin);
            return;
          }
          console.warn("[Friendly Captcha] Retrying agent iframe load.");
          el.src += "&retry=" + retryLoadCounter++;
          registerWithRetry();
        }
      });
    };
    registerWithRetry();
    return agentId;
  }
  /**
   * @internal
   */
  setupPeriodicRefresh() {
    let count = 1;
    setInterval(() => {
      const e = "&expire=" + count++;
      this.agents.forEach((el, origin) => {
        el.src += e;
      });
      this.widgets.forEach((w, id) => {
        const iframe = w.getElement().querySelector("iframe");
        iframe.src += e;
      });
    }, IFRAME_EXP_TIME);
  }
  /**
   * Attaches a widget to given element or elements if they are not attached to yet.
   *
   * You can pass one or more HTML elements to attach to, or undefined. If `undefined` is passed, the HTML page is scanned
   * for unattached widget elements (= elements with the `frc-captcha` class).
   *
   * Returns handles to the newly-attached elements.
   * @public
   */
  attach(elements) {
    if (elements === void 0) {
      elements = findCaptchaElements();
    }
    if (!(Array.isArray(elements) || elements instanceof NodeList)) {
      elements = [elements];
    }
    const newWidgets = [];
    for (let index = 0; index < elements.length; index++) {
      const hElement = elements[index];
      if (hElement && !hElement.frcWidget) {
        const ds = hElement.dataset;
        const opts = {
          element: hElement,
          sitekey: ds.sitekey,
          formFieldName: ds.formFieldName,
          apiEndpoint: ds.apiEndpoint,
          language: ds.lang,
          theme: ds.theme,
          // Perhaps we should we check for valid values?
          startMode: ds.start
          // Perhaps we should we check for valid values?
        };
        newWidgets.push(this.createWidget(opts));
      }
    }
    const allWidgets = this.getAllWidgets();
    this._attached.resolve(allWidgets);
    this.attached = Promise.resolve(allWidgets);
    return newWidgets;
  }
  /**
   * Creates a Friendly Captcha widget with given options under given HTML element.
   * @public
   */
  createWidget(opts) {
    const apiURL = opts.apiEndpoint && resolveAPIEndpoint(opts.apiEndpoint);
    const agentId = this.ensureAgentIFrame(apiURL);
    const widgetId = "w_" + randomId(12);
    const send = (msg) => {
      const msgToSend = { from_id: widgetId, to_id: agentId, _frc: 1 };
      this.bus.send(mergeObject(msgToSend, msg));
    };
    const callbacks = {
      onDestroy: () => {
        send({ type: "root_destroy_widget" });
        this.bus.removeTarget(widgetId);
        this.widgets.delete(widgetId);
        opts.element.innerHTML = "";
        removeWidgetRootStyles(opts.element);
      },
      onReset: () => {
        send({ type: "root_reset_widget" });
      },
      onTrigger: (data) => {
        send({ type: "root_trigger_widget", trigger: data.trigger });
      }
    };
    const registered = flatPromise();
    const widgetHandle = new WidgetHandle({
      id: widgetId,
      createOpts: opts,
      callbacks,
      registered: registered.promise
    });
    this.widgets.set(widgetId, widgetHandle);
    const widgetUrl = this.getAPIUrls(apiURL).widget;
    const wel = createWidgetIFrame(agentId, widgetId, widgetUrl, opts);
    const widgetPlaceholder = createWidgetPlaceholder(opts);
    setWidgetRootStyles(opts.element);
    createBanner(opts);
    const widgetPlaceholderStyle = widgetPlaceholder.style;
    widgetPlaceholder.textContent = "Anti-Robot check connecting...";
    let retryLoadCounter = 1;
    const registerWithRetry = () => {
      this.bus.registerTargetIFrame("widget", widgetId, wel, retryLoadCounter * 2500 + 2e3).then((status) => {
        if (status === "timeout") {
          if (retryLoadCounter > 4) {
            console.error("[Friendly Captcha] Failed to load widget iframe after 4 retries.");
            widgetHandle.setState({
              state: "error",
              response: ".ERROR",
              error: { code: "network_error", detail: "Widget load timeout, stopped retrying" }
            });
            widgetPlaceholderStyle.borderColor = "#f00";
            widgetPlaceholderStyle.fontSize = "12px";
            widgetPlaceholder.textContent = `Anti-Robot check failed to connect to page or ${originOf(
              wel.src
            )}
Check your connection and try again.`;
            return;
          }
          widgetPlaceholderStyle.backgroundColor = "#fee";
          widgetPlaceholderStyle.color = "#222";
          widgetPlaceholder.textContent = `Anti-Robot check took too long to connect.

Retrying... (${retryLoadCounter})`;
          console.warn(`[Friendly Captcha] Retrying widget ${widgetId} iframe load.`);
          widgetHandle.setState({
            state: "error",
            response: ".ERROR",
            error: { code: "network_error", detail: "Widget load timeout, will retry." }
          });
          wel.src += "&retry=" + retryLoadCounter++;
          registerWithRetry();
        } else if (status === "registered") {
          opts.element.removeChild(widgetPlaceholder);
          wel.style.display = "";
        }
      });
    };
    registerWithRetry();
    registered.resolve();
    return widgetHandle;
  }
  /**
   * Returns all current widgets known about (in an unspecified order).
   * @public
   */
  getAllWidgets() {
    const out = [];
    this.widgets.forEach((w) => {
      out.push(w);
    });
    return out;
  }
  /**
   * Retrieves a widget by its widget ID.
   * @public
   */
  getWidgetById(id) {
    return this.widgets.get(id);
  }
  /**
   * Completely remove all widgets and background agents related to the SDK on this page.
   * @public
   */
  clear() {
    this.widgets.forEach((w) => {
      w.destroy();
    });
    this.agents.forEach((el) => {
      el.remove();
    });
    this.agents.clear();
  }
};

// src/sdk/events.ts
var FRCWidgetStateChangeEventName = "frc:widget.statechange";
var FRCWidgetCompleteEventName = "frc:widget.complete";
var FRCWidgetExpireEventName = "frc:widget.expire";
var FRCWidgetErrorEventName = "frc:widget.error";
var FRCWidgetResetEventName = "frc:widget.reset";
export {
  FRCWidgetCompleteEventName,
  FRCWidgetErrorEventName,
  FRCWidgetExpireEventName,
  FRCWidgetResetEventName,
  FRCWidgetStateChangeEventName,
  FriendlyCaptchaSDK
};
/*!
 * Copyright (c) Friendly Captcha GmbH 2023.
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */
